// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0
// Licensed under the Apache License, Version 2.0 (https://www.apache.org/licenses/LICENSE-2.0).

// Document preview beside the extracted values (design 3.1; enlarge-preview-annotate task 9.1). The document is loaded
// once with `useLoadedDocument` and rendered with `PageImage`: PDF pages through pdf.js, PNG/JPEG natively, and the
// first page of a TIFF on a canvas. The page image is one keyboard-focusable control that opens the Enlarged_Viewer
// from the same loaded document (Req 1.1-1.3, 10.1), with an "Enlarge" button beside the page navigation (Req 1.4).
// With an `annotations` model, the Annotation_Layer is drawn over the page actually shown (Req 6.1, 6.9).
import { useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent, type RefObject } from 'react';
import Box from '@cloudscape-design/components/box';
import Button, { type ButtonProps } from '@cloudscape-design/components/button';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Spinner from '@cloudscape-design/components/spinner';
import Toggle from '@cloudscape-design/components/toggle';

import { AnnotationLayer } from '../features/annotations/AnnotationLayer';
import type { PageSize } from '../features/annotations/layout';
import {
  annotationsForPage,
  availability,
  labelsWithMember,
  type AnnotationModel,
} from '../features/annotations/model';
import { useShowAnnotations, useShowValues } from '../features/annotations/settings';
import { useConfidenceThreshold } from '../features/runs/confidenceThreshold';
import { EnlargedViewer, LOCATIONS_UNAVAILABLE } from './EnlargedViewer';
import { useLoadedDocument, type LoadedDocument } from './loadedDocument';
import { PageImage } from './PageImage';
import { inPageRange } from './viewerState';

const MAX_PDF_WIDTH = 560;

/**
 * Width available to the page, capped at MAX_PDF_WIDTH. A fixed 560 px page overflowed the column at a 768 px
 * viewport and made the whole page scroll sideways (requirement 9.1, task 12.2).
 */
function useAvailableWidth(): [RefObject<HTMLDivElement | null>, number] {
  const ref = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(MAX_PDF_WIDTH);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(([entry]) => {
      const w = Math.floor(entry?.contentRect.width ?? MAX_PDF_WIDTH);
      if (w > 0) setWidth(Math.min(MAX_PDF_WIDTH, w));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, width];
}

function failureText(contentType: string): string {
  if (contentType === 'application/pdf') return 'The PDF could not be displayed.';
  if (contentType === 'image/tiff') return 'The TIFF could not be displayed.';
  return 'The image could not be displayed.';
}

type Shown = Exclude<LoadedDocument, { kind: 'loading' | 'error' }>;
type Opener = 'image' | 'button';

/**
 * `goTo` moves the preview to a page (a new `seq` repeats the jump), for example a segment's first page. A jump past
 * the page count is ignored (Req 8.9); one received before the document loads is applied after load if in range.
 * `annotations` (optional) adds the Annotation_Layer and its settings; without it no annotation UI is shown.
 */
export function DocumentViewer({
  url,
  contentType,
  name,
  goTo,
  annotations,
}: {
  url: string;
  contentType: string;
  name: string;
  goTo?: { page: number; seq: number };
  annotations?: AnnotationModel;
}) {
  const loaded = useLoadedDocument(url, contentType);
  const [box, width] = useAvailableWidth();
  const shown: Shown | null = loaded.kind === 'loading' || loaded.kind === 'error' ? null : loaded;
  // A TIFF shows its first page only (Req 2.3, 9.8).
  const pageCount = shown ? (shown.kind === 'tiff' ? 1 : shown.pageCount) : 0;

  const [page, setPage] = useState(1);
  // A new document starts at page 1 (state adjusted during render, not in an effect).
  const [source, setSource] = useState(url);
  if (source !== url) {
    setSource(url);
    setPage(1);
  }

  // A new jump request moves the page when it is in range; before load it waits for the page count.
  const [jumpSeq, setJumpSeq] = useState(goTo?.seq);
  const [pendingJump, setPendingJump] = useState<number | null>(null);
  if (goTo && goTo.seq !== jumpSeq) {
    setJumpSeq(goTo.seq);
    if (!shown) setPendingJump(goTo.page);
    else if (inPageRange(goTo.page, pageCount)) setPage(goTo.page);
  }
  if (shown && pendingJump !== null) {
    setPendingJump(null);
    if (inPageRange(pendingJump, pageCount)) setPage(pendingJump);
  }

  // The page last rendered and the last page failure, each tied to the loaded document they came from.
  const [rendered, setRendered] = useState<{ doc: Shown; page: number; size: PageSize } | null>(null);
  const [failed, setFailed] = useState<{ doc: Shown; page: number } | null>(null);
  const onRendered = useCallback(
    (size: PageSize, p: number) => {
      if (shown) setRendered({ doc: shown, page: p, size });
    },
    [shown],
  );
  const onError = useCallback(() => {
    if (shown) setFailed({ doc: shown, page });
  }, [shown, page]);
  const pageFailed = failed !== null && failed.doc === shown && failed.page === page;
  const current = rendered !== null && rendered.doc === shown && rendered.page === page ? rendered : null;
  const enabled = shown !== null && !pageFailed;

  const [showAnnotations, setShowAnnotations] = useShowAnnotations();
  const [showValues, setShowValues] = useShowValues();
  const [threshold] = useConfidenceThreshold();
  const avail = annotations && shown ? availability(annotations, pageCount) : 'none';
  const pageAnnotations = useMemo(
    () => (annotations && shown ? annotationsForPage(annotations, page, pageCount) : []),
    [annotations, shown, page, pageCount],
  );
  const withMember = useMemo(
    () => (annotations ? labelsWithMember(annotations, pageAnnotations) : false),
    [annotations, pageAnnotations],
  );
  const drawLayer = avail === 'available' && showAnnotations && current !== null;

  // One Enlarged_Viewer at a time (Req 1.8); focus goes back to its opener on close (Req 3.3).
  const [enlarged, setEnlarged] = useState<{ opener: Opener } | null>(null);
  const pageAreaRef = useRef<HTMLDivElement>(null);
  const enlargeRef = useRef<ButtonProps.Ref>(null);
  const focusAfterClose = useRef<Opener | null>(null);
  const open = (opener: Opener) => {
    if (!enabled || enlarged) return;
    setEnlarged({ opener });
  };
  const onClose = (lastPage: number) => {
    focusAfterClose.current = enlarged?.opener ?? 'button';
    setEnlarged(null);
    if (inPageRange(lastPage, pageCount)) setPage(lastPage);
  };
  // Runs after the dialog has unmounted (and after its own focus restore), so this focus wins.
  useEffect(() => {
    if (enlarged || !focusAfterClose.current) return;
    const opener = focusAfterClose.current;
    focusAfterClose.current = null;
    const area = pageAreaRef.current;
    if (opener === 'image' && area?.isConnected) area.focus();
    else enlargeRef.current?.focus();
  }, [enlarged]);

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    e.preventDefault();
    open('image');
  };

  return (
    <SpaceBetween size="s">
      <div ref={box} style={{ maxWidth: '100%', overflow: 'hidden' }}>
        <div
          ref={pageAreaRef}
          role="button"
          tabIndex={enabled ? 0 : -1}
          aria-disabled={enabled ? undefined : true}
          aria-label={`Enlarge page ${page}`}
          onClick={() => open('image')}
          onKeyDown={onKeyDown}
          style={{
            position: 'relative',
            width: shown && !pageFailed ? width : undefined,
            maxWidth: '100%',
            cursor: enabled ? 'zoom-in' : undefined,
          }}
        >
          {loaded.kind === 'loading' && <Spinner />}
          {(loaded.kind === 'error' || pageFailed) && <Box>{failureText(contentType)}</Box>}
          {shown && !pageFailed && (
            <>
              <PageImage
                loaded={shown}
                page={page}
                width={width}
                name={name}
                onRendered={onRendered}
                onError={onError}
              />
              {drawLayer && current && (
                <AnnotationLayer
                  annotations={pageAnnotations}
                  size={current.size}
                  showValues={showValues}
                  withMember={withMember}
                  threshold={threshold}
                  focusable={false}
                  fontSize={10}
                />
              )}
            </>
          )}
        </div>
      </div>
      <SpaceBetween direction="horizontal" size="xs" alignItems="center">
        {pageCount > 1 && (
          <>
            <Button
              iconName="angle-left"
              variant="icon"
              ariaLabel="Previous page"
              disabled={page <= 1}
              onClick={() => setPage(page - 1)}
            />
            <Box>
              <span aria-live="polite">
                Page {page} of {pageCount}
              </span>
            </Box>
            <Button
              iconName="angle-right"
              variant="icon"
              ariaLabel="Next page"
              disabled={page >= pageCount}
              onClick={() => setPage(page + 1)}
            />
          </>
        )}
        <Button ref={enlargeRef} iconName="expand" disabled={!enabled} onClick={() => open('button')}>
          Enlarge
        </Button>
      </SpaceBetween>
      {avail === 'available' && (
        <SpaceBetween direction="horizontal" size="m">
          <Toggle checked={showAnnotations} onChange={({ detail }) => setShowAnnotations(detail.checked)}>
            Show field annotations
          </Toggle>
          {showAnnotations && (
            <Toggle checked={showValues} onChange={({ detail }) => setShowValues(detail.checked)}>
              Show values in labels
            </Toggle>
          )}
        </SpaceBetween>
      )}
      {avail === 'unavailable' && <Box color="text-body-secondary">{LOCATIONS_UNAVAILABLE}</Box>}
      {enlarged && shown && (
        <EnlargedViewer
          name={name}
          loaded={shown}
          pageCount={pageCount}
          initialPage={page}
          annotations={annotations}
          onClose={onClose}
        />
      )}
    </SpaceBetween>
  );
}
